import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, LogOut, User, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { isPro } from "@/lib/provenance";

// Le bloc de compte, seul endroit du site où vivent « Connexion », « Mon
// compte » et l'entrée professionnelle (RDEC-019). La navigation n'en porte
// aucun : c'est ce qui les faisait apparaître en double.

// Un nom par état : l'espace d'un professionnel validé, l'invitation à le
// devenir pour un compte, la porte d'entrée pour un visiteur.
const proEntry = (user, t) => {
  if (user && isPro(user)) return { to: "/pro", label: t("header.proArea") };
  if (user) return { to: "/professionnel", label: t("header.becomePro") };
  return { to: "/professionnel", label: t("header.professionals") };
};

// Connecté, le prénom ouvre un petit menu : la barre garde la place de la
// navigation, et la déconnexion reste à un clic. Il se referme à Échap, au clic
// extérieur et sur chacune de ses entrées.
const AccountMenu = ({ user, logout, t }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const toggleRef = useRef(null);
  const panelId = useId();
  const pro = proEntry(user, t);
  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const base =
    "flex items-center gap-3 min-h-[44px] px-5 text-xs tracking-[0.15em] uppercase hover:text-gold-ink hover:bg-ink/5 transition-colors duration-200";
  const item = `${base} text-ink-muted`;

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={toggleRef}
        type="button"
        data-testid="header-account-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t("header.accountMenu")}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 min-h-[44px] text-xs tracking-[0.12em] uppercase text-gold-ink hover:text-gold-ink transition-colors duration-300"
      >
        <User size={14} />
        <span data-testid="header-user-name">{user.name?.split(" ")[0] || t("header.account")}</span>
        <ChevronDown
          size={12}
          className={`opacity-60 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        id={panelId}
        data-testid="header-account-menu"
        className={`${open ? "block" : "hidden"} absolute right-0 top-full mt-3 min-w-[230px] border border-ink/10 bg-surface/95 backdrop-blur-xl py-2 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.25)]`}
      >
        <Link to="/mon-compte" onClick={close} data-testid="header-account-link" className={item}>
          <User size={14} />
          {t("header.account")}
        </Link>
        <Link to={pro.to} onClick={close} data-testid="header-pro-link" className={item}>
          <Briefcase size={14} />
          {pro.label}
        </Link>
        {user.is_admin && (
          <Link to="/admin" onClick={close} data-testid="header-admin-link" className={item}>
            {t("header.admin")}
          </Link>
        )}
        <button
          type="button"
          data-testid="header-logout-button"
          onClick={() => {
            close();
            logout();
          }}
          className={`${base} w-full mt-1 pt-1 border-t border-ink/10 text-ink-dim`}
        >
          <LogOut size={14} />
          {t("header.logout")}
        </button>
      </div>
    </div>
  );
};

export const AuthControl = ({
  loginRedirect = "/enregistrer",
  variant = "inline",
  // Le menu mobile se referme sur chaque entrée de navigation ; les entrées de
  // compte restaient ouvertes derrière la page qu'elles venaient d'ouvrir, et
  // après une déconnexion le menu restait affiché comme si rien ne s'était
  // passé. Sans effet en variante « inline », qui ne vit dans aucun menu.
  onNavigate,
}) => {
  const { user, loading, logout } = useAuth();
  const { t } = useLanguage();

  if (loading) return null;

  const loginTarget = `/login?redirect=${encodeURIComponent(loginRedirect)}`;
  const pro = proEntry(user, t);

  // Stack variant: full-width, labelled text links for the mobile burger menu,
  // matching the format of the other menu entries.
  if (variant === "stack") {
    const item =
      "flex items-center gap-3 py-3 text-sm tracking-[0.18em] uppercase text-ink-body hover:text-gold-ink transition-colors duration-300";
    const proLink = (
      <Link to={pro.to} onClick={onNavigate} data-testid="menu-pro-link" className={item}>
        <Briefcase size={16} />
        {pro.label}
      </Link>
    );
    if (!user) {
      return (
        <div className="flex flex-col">
          {proLink}
          <Link
            to={loginTarget}
            onClick={onNavigate}
            data-testid="menu-login-link"
            className={item}
          >
            <User size={16} />
            {t("header.login")}
          </Link>
        </div>
      );
    }
    return (
      <div className="flex flex-col">
        <Link
          to="/mon-compte"
          onClick={onNavigate}
          data-testid="menu-account-link"
          className={item}
        >
          <User size={16} />
          {t("header.account")}
        </Link>
        {proLink}
        {user.is_admin && (
          <Link to="/admin" onClick={onNavigate} data-testid="menu-admin-link" className={item}>
            {t("header.admin")}
          </Link>
        )}
        {/* Logout kept visually distinct from "account": separated + muted. */}
        <button
          type="button"
          data-testid="menu-logout-button"
          onClick={() => {
            onNavigate?.();
            logout();
          }}
          className="mt-2 pt-4 border-t border-ink/10 flex items-center gap-3 py-3 text-sm tracking-[0.18em] uppercase text-ink-dim hover:text-gold-ink transition-colors duration-300"
        >
          <LogOut size={16} />
          {t("header.logout")}
        </button>
      </div>
    );
  }

  if (user) return <AccountMenu user={user} logout={logout} t={t} />;

  const link =
    "whitespace-nowrap text-xs tracking-[0.2em] uppercase text-ink-body hover:text-gold-ink transition-colors duration-300";
  return (
    <div className="flex items-center gap-5">
      <Link to={pro.to} data-testid="header-pro-link" className={link}>
        {pro.label}
      </Link>
      <Link to={loginTarget} data-testid="header-login-button" className={link}>
        {t("header.login")}
      </Link>
    </div>
  );
};

export default AuthControl;
